import axios from 'axios';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { config } from '../../config/env';
import traceId from '../../redux/traceId';
import type { RootState } from '../../redux/types';

type FlagContext = Record<string, string | number>;

type FlagVariant = {
  name: string;
  enabled: boolean;
};

type FetchFlagOptions = {
  timeout?: number;
};

const requestHeaders = () => ({
  'x-api-key': config.featureFlag.featureFlagApiKey,
  'x-trace-id': traceId,
});

export const fetchFlagEnabled = async (
  flagName: string,
  { timeout }: FetchFlagOptions = {},
): Promise<boolean> => {
  try {
    const { data } = await axios.get<boolean>(
      `${config.featureFlag.featureFlagApiUrl}/unleash/is-enabled/${flagName}`,
      { headers: requestHeaders(), timeout },
    );
    return data;
  } catch (error) {
    console.log('error ao buscar flags', error);
    return false;
  }
};

export const fetchUserFlagEnabled = async (
  flagName: string,
  userId?: string | number,
): Promise<boolean> => {
  try {
    if (!userId) {
      console.warn(
        `useFeatureFlag: userId is required for permission flag "${flagName}"`,
      );
      return false;
    }
    const { data } = await axios.get<boolean>(
      `${config.featureFlag.featureFlagApiUrl}/unleash/has-permission-by-user-id/${flagName}/${userId}`,
      { headers: requestHeaders() },
    );
    return data;
  } catch (error) {
    console.log('error ao buscar flags', error);
    return false;
  }
};

export type UnleashFeatureFlagAdapter = {
  isEnabled: (flagName: string) => Promise<boolean>;
  isUserEnabled: (flagName: string) => Promise<boolean>;
  rolloutReleaseByContextDirectly: (
    flagName: string,
    context: FlagContext,
  ) => Promise<boolean>;
  getVariantDirectly: (
    flagName: string,
    context: FlagContext,
  ) => Promise<FlagVariant>;
  flagsReady: boolean;
};

const useUnleashFeatureFlagAdapter = (): UnleashFeatureFlagAdapter => {
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const baseUrl = config.featureFlag.featureFlagApiUrl;

  return useMemo(
    () => ({
      isEnabled: (flagName: string) => fetchFlagEnabled(flagName),
      isUserEnabled: (flagName: string) =>
        fetchUserFlagEnabled(flagName, userId),
      rolloutReleaseByContextDirectly: async (
        flagName: string,
        context: FlagContext,
      ) => {
        try {
          if (!context) {
            console.warn(
              `useFeatureFlag: context is required for permission flag "${flagName}"`,
            );
            return false;
          }
          const { data } = await axios.post<boolean>(
            `${baseUrl}/unleash/direct/rollout-release-by-context/${flagName}`,
            { context },
            { headers: requestHeaders() },
          );
          return data;
        } catch (error) {
          console.log('error ao buscar flags', error);
          return false;
        }
      },
      getVariantDirectly: async (flagName: string, context: FlagContext) => {
        try {
          if (!context || Object.keys(context).length === 0) {
            console.warn(
              `useFeatureFlag: context is required for variant flag "${flagName}"`,
            );
            return { name: 'disabled', enabled: false };
          }
          const { data } = await axios.post<FlagVariant>(
            `${baseUrl}/unleash/direct/variant/${flagName}`,
            { context },
            { headers: requestHeaders() },
          );
          return data;
        } catch (error) {
          console.log('error ao buscar variant', error);
          return { name: 'disabled', enabled: false };
        }
      },
      flagsReady: true,
    }),
    [userId, baseUrl],
  );
};

export default useUnleashFeatureFlagAdapter;
